/**
 * Homly — ActionCard
 * ==================
 * Carte d'action prioritaire affichée dans les dashboards.
 * Centre l'utilisateur sur la prochaine action concrète à effectuer.
 *
 * Pattern : Priority Badge · Titre clair · Contexte court · Bouton CTA
 *
 * Usage type :
 *   <ActionCard
 *     priority="high"
 *     title="Relancer M. Dupont"
 *     context="Mandat exclusif au Prado · sans contact depuis 12 jours"
 *     ctaLabel="Préparer la relance"
 *     onCtaClick={() => navigate('/admin/mandates/abc')}
 *     icon={Phone}
 *   />
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { PriorityBadge } from './Badges';

export function ActionCard({
    priority = 'medium',
    title,
    context,
    ctaLabel = 'Voir',
    icon: Icon,
    onCtaClick,
    onClick,
    timestamp,
    className = '',
    dataTestid,
}) {
    const clickable = !!(onCtaClick || onClick);
    const handleClick = onCtaClick || onClick;
    return (
        <article
            onClick={handleClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={(e) => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleClick(e);
                }
            }}
            data-testid={dataTestid || 'action-card'}
            className={className}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--homly-space-4)',
                padding: 'var(--homly-space-4) var(--homly-space-5)',
                background: 'var(--homly-bg-elevated)',
                border: '1px solid var(--homly-border)',
                borderRadius: 'var(--homly-radius-lg)',
                cursor: clickable ? 'pointer' : 'default',
                transition: 'all var(--homly-transition-base)',
                fontFamily: 'var(--homly-font-body)',
            }}
            onMouseEnter={(e) => {
                if (!clickable) return;
                e.currentTarget.style.borderColor = 'var(--homly-purple-border)';
                e.currentTarget.style.background = 'var(--homly-bg-card)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 85, 247, 0.12)';
            }}
            onMouseLeave={(e) => {
                if (!clickable) return;
                e.currentTarget.style.borderColor = 'var(--homly-border)';
                e.currentTarget.style.background = 'var(--homly-bg-elevated)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            {/* Icon left rail */}
            {Icon && (
                <div
                    style={{
                        position: 'relative',
                        flexShrink: 0,
                        width: 36,
                        height: 36,
                        borderRadius: 'var(--homly-radius-md)',
                        background: 'var(--homly-bg-card-2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--homly-purple)',
                    }}
                >
                    <Icon size={18} strokeWidth={2} aria-hidden="true" />
                    {priority === 'critical' && (
                        <span
                            aria-hidden="true"
                            className="homly-pulse-urgent"
                            style={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: 'var(--homly-priority-critical)',
                                boxShadow: '0 0 6px var(--homly-priority-critical)',
                            }}
                        />
                    )}
                </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--homly-space-2)',
                        marginBottom: 'var(--homly-space-2)',
                        flexWrap: 'wrap',
                    }}
                >
                    <PriorityBadge priority={priority} size="sm" />
                    {timestamp && (
                        <span
                            style={{
                                fontSize: 11,
                                color: 'var(--homly-text-tertiary)',
                                fontFamily: 'var(--homly-font-mono)',
                            }}
                        >
                            {timestamp}
                        </span>
                    )}
                </div>
                <h3
                    style={{
                        margin: 0,
                        fontSize: 15,
                        fontWeight: 600,
                        color: 'var(--homly-text-primary)',
                        fontFamily: 'var(--homly-font-display)',
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </h3>
                {context && (
                    <p
                        style={{
                            margin: '4px 0 0 0',
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: 'var(--homly-text-secondary)',
                        }}
                    >
                        {context}
                    </p>
                )}
            </div>

            {/* CTA */}
            {clickable && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClick(e);
                    }}
                    aria-label={ctaLabel}
                    style={{
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '8px 14px',
                        background: 'var(--homly-gradient-purple)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--homly-radius-md)',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'var(--homly-font-display)',
                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.25)',
                        transition: 'transform var(--homly-transition-fast)',
                        whiteSpace: 'nowrap',
                    }}
                    data-testid={`${dataTestid || 'action-card'}-cta`}
                >
                    {ctaLabel}
                    <ChevronRight size={14} strokeWidth={2.5} />
                </button>
            )}
        </article>
    );
}
